import { EventType, PublicClientApplication } from '@azure/msal-browser';
import { MsalProvider } from '@azure/msal-react';
import CssBaseline from '@mui/material/CssBaseline';
import { ThemeProvider } from '@mui/material/styles';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { App } from './App';
import { msalConfig } from './auth/msalConfig';
import { setMsalInstance } from './auth/tokenProvider';
import { ApiError } from './api/ApiError';
import { theme } from './theme';

const msalInstance = new PublicClientApplication(msalConfig);

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Az üzleti hibák (4xx) újrapróbálása értelmetlen: ugyanaz a válasz
      // jönne. Csak a hálózati hibát próbáljuk újra, egyszer.
      retry: (failureCount, error) =>
        error instanceof ApiError && error.code === 'NETWORK_ERROR' && failureCount < 1,
      refetchOnWindowFocus: false,
    },
    mutations: {
      // Írásnál SOHA nem próbálunk újra automatikusan: az újrapróbálásnak
      // ugyanazzal az idempotencia-kulccsal kell történnie, ami a hívó
      // felelőssége (ld. `api/client.ts`).
      retry: false,
    },
  },
});

/**
 * Az MSAL-t a React-fa előtt kell inicializálni (v3+ kötelező), és a
 * redirect-válasz feldolgozása is itt történik — enélkül a bejelentkezésből
 * visszatérve nem lenne aktív fiók.
 */
async function bootstrap(): Promise<void> {
  await msalInstance.initialize();
  setMsalInstance(msalInstance);

  const accounts = msalInstance.getAllAccounts();
  if (accounts.length > 0 && accounts[0]) {
    msalInstance.setActiveAccount(accounts[0]);
  }

  msalInstance.addEventCallback((event) => {
    if (event.eventType === EventType.LOGIN_SUCCESS && event.payload) {
      const payload = event.payload as { account?: Parameters<typeof msalInstance.setActiveAccount>[0] };
      if (payload.account) msalInstance.setActiveAccount(payload.account);
    }
  });

  await msalInstance.handleRedirectPromise();

  const container = document.getElementById('root');
  if (!container) throw new Error('Hiányzik a #root elem.');

  createRoot(container).render(
    <StrictMode>
      <MsalProvider instance={msalInstance}>
        <QueryClientProvider client={queryClient}>
          <ThemeProvider theme={theme}>
            <CssBaseline />
            <App />
          </ThemeProvider>
        </QueryClientProvider>
      </MsalProvider>
    </StrictMode>,
  );
}

void bootstrap();
